import React, { useState } from 'react';
import { Chip, styled, useTheme } from '@mui/material';
import { Typography } from '../../atoms/Typography/Typography';
import { UniversalCard } from '../UniversalCard/UniversalCard';
import { useTranslation } from 'react-i18next';
import { ConfirmationCentered } from '../ConfirmationDialog/ConfirmationDialog';
import { useAppSelector, useAppDispatch } from '../../../common/hooks';
import { saveGoal } from '../../../features/client-goals';
import {
    selectAffectedEwsTerms,
    selectEwsRequests,
    selectEwsResponses,
    setAffectedEwsTerms,
    setEwsMaps,
    setEwsStepperCtaStatus,
    computeEwsStepperCtaStatus
} from '../../../features/ews';
import {
    checkFFEnabled,
    formatCurrency,
    getAffectedEwsTerms,
    getPriorityLabel,
    getGoalPriorityEnum,
    getPriorityColor,
    priorityOptionsFlow2,
    pruneGoalFromEwsState
} from '../../../utils';
import {
    EWS_DURATION_MAP,
    EWS_TERM_TAB_KEY_MAP,
    FEATURE_NAMES,
    invalidDateText,
    TermTabType
} from '../../../common/constants';
import { Flow2GoalPriorityEnum } from '../../../common/types';

export interface GoalSummaryChipProps {
    headerIcon: React.ReactNode | React.ReactNode[];
    goalType: string;
    isRetirement?: boolean;
    goal_name: string;
    priority?: Flow2GoalPriorityEnum;
    goalAmount: number;
    target_date_data: string;
    retirement_age?: number;
    goalId: string;
    targetedRetirementIncome?: number;
    planningDate: string | null;
    // eslint-disable-next-line no-unused-vars
    onGoalDelete: (goalId: string) => void;
    onEdit?: () => void;
    showErrorBorder?: boolean;
    frequency?: string;
    isIncomeGoal?: boolean;
}

const StyledDivLeft = styled('div')({
    display: 'flex',
    justifyContent: 'left',
    flexDirection: 'column',
    marginRight: '0.75rem'
});

const StyledDivRight = styled('div')({
    display: 'flex',
    justifyContent: 'right',
    alignItems: 'center',
    width: '100%'
});

const StyledContentContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    marginTop: '20px',
    gap: '20px'
});

const StyledDivPriority = styled('div')({
    display: 'flex',
    width: '100%'
});

export const styledMainDiv = styled('div')({
    paddingTop: '0px'
});

export const StyledTable = styled('table')({
    width: '100%',
    borderCollapse: 'collapse',
    '& tr': {
        verticalAlign: 'top',
        '& td': {
            padding: 0
        }
    }
});

export const StyledTargetedIncomeCell = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    '& td': {
        display: 'flex',
        flexDirection: 'row',
        gap: '6px'
    }
});

export const GoalSummaryChipFlow2 = ({
    goal_name,
    goalType,
    headerIcon,
    priority,
    goalAmount,
    target_date_data,
    planningDate,
    isRetirement,
    isIncomeGoal,
    goalId,
    onGoalDelete,
    onEdit,
    targetedRetirementIncome,
    showErrorBorder,
    frequency
}: GoalSummaryChipProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const dispatch = useAppDispatch();
    const { goeConfig } = useAppSelector((state) => state.global);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const ewsRequestsState = useAppSelector(selectEwsRequests);
    const ewsResponsesState = useAppSelector(selectEwsResponses);
    const affectedEwsTermsState = useAppSelector(selectAffectedEwsTerms);
    const onConfirmDelete = () => {
        const remainingFormFields = adviceGoals.formFields.filter((goal) => goal.goalId !== goalId);
        const formFieldsForDelete = checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
            ? remainingFormFields.map((goal: any) =>
                  goal.goalId ? { ...goal, data: { ...goal.data, update: false } } : goal
              )
            : remainingFormFields;
        // Remove ONLY the deleted goal from every downstream step's data, leaving all other goals
        // intact: runpipe request/response, the goal-calculator results and the per-term EWS
        // request/response maps. planExecutionResponse is account-aggregated (no per-goal slice),
        // so it is intentionally left as-is until the plan is re-run.
        const remainingRequest = (adviceGoals.request ?? []).filter((item: any) => item?.goalId !== goalId);
        const remainingResponse = (adviceGoals.response ?? []).filter((item: any) => item?.goalId !== goalId);
        const remainingResponseGoalCalculator = (adviceGoals.global?.responseGoalCalculator ?? []).filter(
            (item: any) => item?.goalId !== goalId
        );
        const { ewsRequests: remainingEwsRequests, ewsResponses: remainingEwsResponses } = pruneGoalFromEwsState(
            ewsRequestsState,
            ewsResponsesState,
            goalId
        );
        // Derive the deleted goal's EWS term so remaining goals in that term are re-evaluated: deleting
        // a goal frees its funding sources, which changes the funding math for the other goals in the
        // same term, so that term must be flagged affected (mirrors the ADD/EDIT flow's
        // getAffectedEwsTerms).
        const deletedGoal = adviceGoals.formFields.find((goal) => goal.goalId === goalId);
        const deletedGoalOrder = deletedGoal?.data?.goalCategoryOrder;
        const deletedGoalCategory =
            typeof deletedGoalOrder === 'number'
                ? EWS_DURATION_MAP[EWS_TERM_TAB_KEY_MAP[(deletedGoalOrder - 1) as TermTabType]]
                : deletedGoal?.data?.goalCategory ?? null;
        const affectedEwsTerms = getAffectedEwsTerms(affectedEwsTermsState, deletedGoalCategory);
        // Update the per-term EWS maps + affected-terms flag in the dedicated slice; saveGoal
        // reconstructs global.ews*/affectedEwsTerms from this slice, so the persisted document stays in sync.
        dispatch(setEwsMaps({ requests: remainingEwsRequests, responses: remainingEwsResponses }));
        // Keep the aggregate stepper/CTA rollup consistent with the pruned responses.
        dispatch(setEwsStepperCtaStatus(computeEwsStepperCtaStatus(remainingEwsResponses)));
        dispatch(setAffectedEwsTerms(affectedEwsTerms));
        let updatedGoalData = {
            ...adviceGoals,
            formFields: formFieldsForDelete,
            request: remainingRequest,
            response: remainingResponse,
            global: {
                ...adviceGoals.global,
                responseGoalCalculator: remainingResponseGoalCalculator
            }
        };
        dispatch(saveGoal(updatedGoalData));
        setShowDeleteModal(false);
        onGoalDelete(goalId);
    };

    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const goalPriorityEnum = getGoalPriorityEnum(goalPriorityValue);
    const priorityOption = priorityOptionsFlow2(t, goalPriorityEnum).find((option) => option.value === priority) || {
        index: 0,
        label: '',
        value: ''
    };
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const priorityLabel = getPriorityLabel(priorityOption, tenant, t);

    return (
        <UniversalCard
            header={t(
                goalType === 'wealth'
                    ? 'WEALTH_GOAL_CARD_HEADER'
                    : goalType === 'income'
                    ? 'INCOME_GOAL_CARD_HEADER'
                    : 'RETIREMENT_GOAL_CARD_HEADER'
            )}
            headerIcon={headerIcon}
            actionMenuOptions={[
                {
                    label: t('LABEL_EDIT'),
                    onClick: () => {
                        if (onEdit) onEdit();
                    },
                    icon: 'edit'
                },
                {
                    label: t('DELETE_TEXT'),
                    onClick: () => setShowDeleteModal(!showDeleteModal),
                    icon: 'delete'
                }
            ]}
            onClickViewAll={() => {}}
            showSubcontent={false}
            cardSx={{
                padding: '20px 24px',
                '& .MuiCardHeader-root': { paddingTop: 0 },
                '& .MuiCardHeader-action': { margin: 0, alignSelf: 'center' }
            }}
            showErrorBorder={showErrorBorder}
        >
            <StyledContentContainer>
                <div>
                    <StyledDivPriority>
                        <Typography
                            variant="labels.large"
                            label={t('GOAL_SUMMARY_DETAIL_TEXT')}
                            sx={{ color: theme.palette.grey[300], width: '100%' }}
                            mb="4px"
                        />
                        <StyledDivRight>
                            {priority && (
                                <Chip
                                    label={`${priorityOption?.index + 1} - ${priorityLabel}`}
                                    size="small"
                                    sx={{
                                        backgroundColor: getPriorityColor(theme, priorityLabel),
                                        fontWeight: 600,
                                        minWidth: '100px',
                                        borderRadius: '16px'
                                    }}
                                />
                            )}
                        </StyledDivRight>
                    </StyledDivPriority>
                    <StyledDivLeft>
                        <Typography variant="headers.h4" color="neutral.black" label={goal_name} />
                    </StyledDivLeft>
                </div>
                <StyledTable>
                    <tr>
                        {(isRetirement || isIncomeGoal) && targetedRetirementIncome && (
                            <td>
                                <Typography
                                    variant="labels.large"
                                    label={t('INCOME_GOAL_AMOUNT')}
                                    sx={{ color: theme.palette.grey[300] }}
                                    mb="4px"
                                />
                            </td>
                        )}
                        {goalAmount ? (
                            <td>
                                <Typography
                                    variant="labels.large"
                                    label={t(isRetirement ? 'GOAL_SUMMARY_BEQUEST_AMOUNT_TEXT' : 'INCOME_GOAL_AMOUNT')}
                                    sx={{ color: theme.palette.grey[300] }}
                                    mb="4px"
                                />
                            </td>
                        ) : null}
                    </tr>
                    <tr>
                        {(isRetirement || isIncomeGoal) && targetedRetirementIncome && (
                            <StyledTargetedIncomeCell>
                                <td>
                                    <Typography
                                        variant="headers.h4"
                                        color="neutral.black"
                                        label={
                                            frequency === t('MONTHLY')
                                                ? formatCurrency(targetedRetirementIncome)
                                                : formatCurrency(Math.round(Number(targetedRetirementIncome! / 12)))
                                        }
                                    />
                                    <Typography
                                        variant="labels.small"
                                        color="neutral.black"
                                        label={t('MONTHLY')}
                                        mt="6px"
                                    />
                                    <Typography variant="headers.h4" color="neutral.black" label={' |'} />
                                </td>
                                <td>
                                    <Typography
                                        variant="headers.h4"
                                        color="neutral.black"
                                        label={
                                            frequency === t('ANNUALLY')
                                                ? formatCurrency(targetedRetirementIncome)
                                                : formatCurrency(targetedRetirementIncome! * 12)
                                        }
                                    />
                                    <Typography
                                        variant="labels.small"
                                        color="neutral.black"
                                        label={t('ANNUAL')}
                                        mt="6px"
                                    />
                                </td>
                            </StyledTargetedIncomeCell>
                        )}
                        {goalAmount ? (
                            <td>
                                <Typography
                                    variant="headers.h4"
                                    color="neutral.black"
                                    label={formatCurrency(goalAmount)}
                                />
                            </td>
                        ) : null}
                    </tr>
                    <tr style={{ height: '20px' }}></tr>
                    <tr>
                        <td>
                            <Typography
                                variant="labels.large"
                                label={isRetirement || isIncomeGoal ? t('STARTING_AT_LABEL') : t('ENDING_AT_LABEL')}
                                sx={{ color: theme.palette.grey[300] }}
                                mb="4px"
                            />
                        </td>
                        {(isRetirement || isIncomeGoal) && planningDate && (
                            <td>
                                <Typography
                                    variant="labels.large"
                                    label={t('ENDING_AT_LABEL')}
                                    sx={{ color: theme.palette.neutral[300] }}
                                    mb="4px"
                                />
                            </td>
                        )}
                    </tr>
                    <tr>
                        <td>
                            <Typography
                                variant="headers.h4"
                                color="neutral.black"
                                label={
                                    target_date_data && target_date_data !== invalidDateText ? target_date_data : '-'
                                }
                            />
                        </td>
                        {(isRetirement || isIncomeGoal) && planningDate && (
                            <td>
                                <Typography
                                    variant="headers.h4"
                                    color="neutral.black"
                                    label={planningDate && planningDate !== invalidDateText ? planningDate : '-'}
                                />
                            </td>
                        )}
                    </tr>
                </StyledTable>
            </StyledContentContainer>
            {showDeleteModal && (
                <ConfirmationCentered
                    cancelButtonLabel={`${t('BUTTON_CANCEL')}`}
                    confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                    title={`${t('DELETE_GOAL_TITLE')}`}
                    content={`${t('DELETE_GOAL_CONTENT')}`}
                    onCancellation={() => setShowDeleteModal(false)}
                    onCloseIconClick={() => setShowDeleteModal(false)}
                    onConfirmation={() => onConfirmDelete()}
                    open
                />
            )}
        </UniversalCard>
    );
};
